import React from 'react';

import {useGate, useUnit} from 'effector-react';
import {useTranslation} from 'react-i18next';
import {Redirect, Route, Switch, useHistory, useParams} from 'react-router-dom';

import {ROUTES} from '@app/routing';
import {colors} from '@app/styles/theme';
import {EMPTY_VALUE as EMPTY} from '@consts/common';
import {STATUS_COLOR_LIST} from '@consts/statuses';
import {AuditViolationGate, auditViolationModel} from '@entities/audit-violation/model';
import {NotificationDetails} from '@pages/Audit/ui/ViolationNotificationsTab';
import {NotificationsDrawerHeader, NotificationsDrawerLoading} from '@pages/TaxpayerDetails/styled';
import {Button, ButtonVariant} from '@shared/ui/Button';
import {Icon} from '@shared/ui/Icon';
import {ContentContainer, StatusWrapper} from '@shared/ui/PageComponents';
import {RouterTab, RouterTabList} from '@shared/ui/RouterTabs';
import {Drawer} from '@shared/ui/Sidebar';
import {Spinner} from '@shared/ui/Spinner';
import {Title as DrawerTitle} from '@shared/ui/Title';

import {localizeViolationTitle, ViolationGeneralInformation} from './ViolationGeneralInformation';
import {
    BackButton,
    HeaderActions,
    HeaderCard,
    SummaryCard,
    SummaryDownloadWrapper,
    SummaryGrid,
    SummaryGroup,
    SummaryLabel,
    SummaryValue,
    Title,
    TitleRow,
    Wrapper,
} from './ViolationGeneralInformation/styled';
import {ViolationHistoryTab} from './ViolationHistoryTab';
import {ViolationNotificationsTab} from './ViolationNotificationsTab';

const violationStatusColor = (code?: string | null) =>
    code === 'CLOSED' ? STATUS_COLOR_LIST.GRAY_ALT : STATUS_COLOR_LIST.GREEN;

const notificationStatusColor = (code?: string | null) =>
    code === 'NEW'
        ? STATUS_COLOR_LIST.ORANGE
        : code === 'SENT'
        ? STATUS_COLOR_LIST.BLUE
        : code === 'SEND_ERROR'
        ? STATUS_COLOR_LIST.RED
        : code === 'READ'
        ? STATUS_COLOR_LIST.GREEN
        : STATUS_COLOR_LIST.GRAY;

export const ViolationDetailsPage = () => {
    const {id} = useParams<{id: string}>();
    const history = useHistory();
    const {t, i18n} = useTranslation();
    const model = useUnit(auditViolationModel);

    useGate(AuditViolationGate, {id});

    const violation = model.state.violation;
    const violationType = model.state.violationTypes.find((item) => item.id === violation?.type);
    const notificationStatus = model.state.notificationStatuses.find(
        (item) =>
            item.id === violation?.notificationStatus ||
            item.code === violation?.notificationStatus,
    );
    const latestNotification = model.state.notifications?.data?.[0];
    const latestStatus = model.state.notificationStatuses.find(
        (item) =>
            item.id === latestNotification?.status || item.code === latestNotification?.status,
    );
    const violationName = localizeViolationTitle(violationType?.title, i18n.language);
    const detailsUrl = ROUTES.AUDIT_VIOLATION_DETAILS.replace(':id', id);

    return (
        <ContentContainer status={model.state.pending ? 'loading' : 'default'} noPaddings>
            <Wrapper>
                <HeaderCard>
                    <HeaderActions>
                        <BackButton
                            type="button"
                            onClick={() => history.push(ROUTES.AUDIT_VIOLATIONS)}
                        >
                            <Icon name="arrowLeft" size={16} color={colors.brandPrimary} />
                            {t('page_audit.page_violations.title')}
                        </BackButton>
                    </HeaderActions>
                    <TitleRow>
                        <Title>
                            {t('page_audit.page_violation.violation-name', {violationName})}
                        </Title>
                        {violation?.status ? (
                            <StatusWrapper color={violationStatusColor(violation.status.code)}>
                                {localizeViolationTitle(violation.status.title, i18n.language)}
                            </StatusWrapper>
                        ) : null}
                    </TitleRow>
                    <SummaryGrid>
                        <SummaryCard
                            type="button"
                            onClick={() => {
                                if (violation?.payerTin) {
                                    history.push(
                                        ROUTES.AUDIT_VIOLATIONS_TAXPAYER_DETAILS.replace(
                                            ':tin',
                                            violation.payerTin,
                                        ),
                                    );
                                }
                            }}
                        >
                            <SummaryGroup>
                                <SummaryLabel>{t('page_audit.terms.taxpayer')}</SummaryLabel>
                                <SummaryValue>{violation?.payerName || EMPTY}</SummaryValue>
                            </SummaryGroup>
                            <SummaryGroup>
                                <SummaryLabel>{t('page_audit.terms.tin')}</SummaryLabel>
                                <SummaryValue>{violation?.payerTin || EMPTY}</SummaryValue>
                            </SummaryGroup>
                        </SummaryCard>

                        <SummaryCard
                            type="button"
                            disabled={!latestNotification}
                            onClick={() => {
                                if (latestNotification) model.latestNotificationOpened();
                            }}
                        >
                            <SummaryGroup>
                                <SummaryLabel>
                                    {t('page_audit.page_violation.last-notification')}
                                </SummaryLabel>
                                <SummaryValue>
                                    <StatusWrapper
                                        color={notificationStatusColor(latestStatus?.code)}
                                    >
                                        {localizeViolationTitle(
                                            latestStatus?.title ?? notificationStatus?.title,
                                            i18n.language,
                                        )}
                                    </StatusWrapper>
                                </SummaryValue>
                            </SummaryGroup>
                            <SummaryGroup>
                                <SummaryLabel>
                                    {t('page_audit.page_violation.notifications-count')}
                                </SummaryLabel>
                                <SummaryDownloadWrapper>
                                    <SummaryValue>
                                        {model.state.notifications?.total ?? 0}
                                    </SummaryValue>
                                    <Button
                                        size="sm"
                                        variant={ButtonVariant.Outline}
                                        disabled={!latestNotification}
                                        isLoading={model.state.notificationDetailsPending}
                                        icon={<Icon name="download" size={16} />}
                                        onClick={(e) => {
                                            e.stopPropagation();
                                            model.latestNotificationDownloadRequested();
                                        }}
                                    >
                                        {t('actions.download')}
                                    </Button>
                                </SummaryDownloadWrapper>
                            </SummaryGroup>
                        </SummaryCard>
                    </SummaryGrid>

                    <RouterTabList baseUrl={detailsUrl}>
                        <RouterTab url="general-information">
                            {t('page_audit.terms.general-information')}
                        </RouterTab>
                        <RouterTab url="notifications">
                            {t('page_audit.terms.notifications')}
                        </RouterTab>
                        <RouterTab url="history">{t('page_audit.terms.history')}</RouterTab>
                    </RouterTabList>
                </HeaderCard>

                <Switch>
                    <Route path={ROUTES.AUDIT_VIOLATION_DETAILS} exact>
                        <Redirect
                            to={ROUTES.AUDIT_VIOLATION_GENERAL_INFORMATION.replace(':id', id)}
                        />
                    </Route>
                    <Route path={ROUTES.AUDIT_VIOLATION_GENERAL_INFORMATION} exact>
                        <ViolationGeneralInformation />
                    </Route>
                    <Route path={ROUTES.AUDIT_VIOLATION_NOTIFICATIONS} exact>
                        <ViolationNotificationsTab />
                    </Route>
                    <Route path={ROUTES.AUDIT_VIOLATION_HISTORY} exact>
                        <ViolationHistoryTab />
                    </Route>
                </Switch>

                <Drawer
                    open={model.state.isNotificationDetailsOpen}
                    onClose={model.notificationDetailsClosed}
                >
                    <NotificationsDrawerHeader>
                        <DrawerTitle size={4}>
                            {t('page_audit.page_violation.notification_letter.type')}
                        </DrawerTitle>
                        {latestStatus && (
                            <StatusWrapper color={notificationStatusColor(latestStatus.code)}>
                                {localizeViolationTitle(latestStatus.title, i18n.language)}
                            </StatusWrapper>
                        )}
                    </NotificationsDrawerHeader>

                    {model.state.notificationDetailsPending && !model.state.selectedMessage ? (
                        <NotificationsDrawerLoading>
                            <Spinner />
                        </NotificationsDrawerLoading>
                    ) : (
                        <NotificationDetails
                            message={model.state.selectedMessage}
                            contents={model.state.contentList}
                            onDownloadAttachment={() => undefined}
                            t={t}
                        />
                    )}
                </Drawer>
            </Wrapper>
        </ContentContainer>
    );
};
